import { Drawer, Empty, Spin, Tag, Timeline, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { maintenanceAuditApi } from '@/features/maintenance/api'
import dayjs from 'dayjs'

const { Text, Paragraph } = Typography

interface Props {
  entityId: string | null
  title?: string
  open: boolean
  onClose: () => void
}

/**
 * Shared right-side drawer that renders the audit trail for any entity
 * (Maintenance Request, Work Order, PM Schedule, etc). The same JSONB
 * `_system_generated` / `_source_module` / `_reason` markers we record
 * in the service layer are surfaced here as visible badges so users can
 * tell *who* (or *what*) made each change.
 */
export default function AuditTrailDrawer({ entityId, title, open, onClose }: Props) {
  const { data, isLoading } = useQuery({
    enabled: !!entityId && open,
    queryKey: ['maintenance-audit', entityId],
    queryFn: () => maintenanceAuditApi.forEntity(entityId!, 200),
  })

  return (
    <Drawer
      title={title ?? 'Audit Trail'}
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
      ) : !data || data.length === 0 ? (
        <Empty description="No audit entries yet" />
      ) : (
        <Timeline
          items={data.map((row) => {
            const after = (row.after_data ?? {}) as Record<string, unknown>
            const isSystem = after._system_generated === true
            const reason = after._reason as string | undefined
            const sourceModule = after._source_module as string | undefined
            return {
              color: isSystem ? 'blue' : 'green',
              children: (
                <div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Text strong>{row.action.replace(/_/g, ' ')}</Text>
                    {isSystem && <Tag color="geekblue">SYSTEM</Tag>}
                    {sourceModule && <Tag>{sourceModule}</Tag>}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {dayjs(row.performed_at).format('DD MMM YYYY HH:mm:ss')}
                  </Text>
                  {reason && (
                    <Paragraph style={{ marginTop: 6, marginBottom: 0 }}>
                      <Text type="secondary">Reason: </Text>{reason}
                    </Paragraph>
                  )}
                  {row.before_data && (
                    <Paragraph style={{ marginTop: 4, marginBottom: 0 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Before: </Text>
                      <code style={{ fontSize: 11 }}>
                        {JSON.stringify(row.before_data, null, 0)}
                      </code>
                    </Paragraph>
                  )}
                </div>
              ),
            }
          })}
        />
      )}
    </Drawer>
  )
}
